import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { formatMediaDateRange } from '../../lib/media';

// School-side cancellation modal (spec §8).
//
// Shows the penalty-window state up front so the school knows whether
// they're forfeiting a credit:
//   ≥48h out → credit restored (no penalty)
//   <48h out → credit consumed (no refund — the slot has held the
//             shooter's calendar already)

export default function CancelEventDialog({ event, onClose, onCancelled }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const startMs = Date.parse(event.scheduledStartAt);
  const withinPenalty = !Number.isNaN(startMs) && (startMs - Date.now()) < 48 * 3600_000;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.post(`/api/client/media/events/${event.id}/cancel`, { reason });
      onCancelled?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to cancel');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Cancel booking</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={event.eventTitle}>
              {event.eventTitle}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="text-[12px] text-gray-400">
            Scheduled: {formatMediaDateRange(event.scheduledStartAt, event.scheduledEndAt)}
          </div>

          <div className={`rounded-lg border px-3 py-2.5 ${
            withinPenalty
              ? 'border-red-400/30 bg-red-500/[0.05] text-red-200'
              : 'border-[#B0FC38]/30 bg-[#B0FC38]/[0.04] text-[#B0FC38]'
          }`}>
            <div className="text-[12px] font-medium">
              {withinPenalty
                ? 'Less than 48 hours out: your credit will be consumed.'
                : '48+ hours out: your credit will be restored.'}
            </div>
            <div className="text-[11px] mt-1 opacity-80">
              {withinPenalty
                ? 'NexSav has already held the shooter\'s calendar for this date. Contact us if this is a weather or emergency cancellation.'
                : 'The credit returns to your package balance for use on a future event.'}
            </div>
          </div>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Reason (optional)</span>
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you cancelling?"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40" />
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">
              Keep booking
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 rounded-full bg-red-500 text-white text-[13px] font-medium hover:bg-red-600 disabled:opacity-50">
              {submitting ? 'Cancelling…' : 'Cancel booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
